// FloatingChat widget — bottom-right docked chat launcher + panel.
//
// Mirrors ads-iq/frontend/src/components/FloatingChat.tsx 1:1: this is now a
// THIN wrapper that iframes the full-page chat at /brand/:slug/chat?embedded=1.
// All chat logic (streaming, markdown, WorkIndicator, avatars, permission
// cards, suggestion chips, default chips, [error] surfacing, region handling)
// lives in pages/Chat.tsx — ONE component is both the full page and the widget
// body, exactly like ads-iq.
//
// Why iframe, not inline render: it's how ads-iq does it, and it keeps the
// chat's URL-driven state (?conv=…, ?embedded=1) self-contained — the host
// page's URL never picks up a `conv` query param. Cookies are same-origin so
// auth + the region cookie/URL Just Work.
//
// // retention adaptation: no platform dimension (Klaviyo only) — the iframe
// // src is /brand/:slug/chat?embedded=1, not ads-iq's per-platform URL. The
// // launcher keeps RetentionIQ's existing "Advisor" pill styling (amber
// // tokens) rather than ads-iq's per-platform solid color.

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, X, Sparkles, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { chatUrl, chatEmbeddedUrl, chatEmbeddedSeedUrl } from "@/utils/urls";

export function ChatPanel() {
  const { slug } = useParams();
  const [open, setOpen] = useState(false);

  // Cross-page "Get advice" entry points (Subjects / Promotions) dispatch a
  // `retention:open-chat` window event with a prompt. Restore the pre-iframe
  // behavior: open the widget AND seed the first turn. Because the chat now
  // lives in the iframe, we hand the prompt off via the URL
  // (/chat?embedded=1&seed_prompt=…) and the embedded Chat auto-sends it.
  // The nonce re-keys the iframe so it reloads (and re-seeds) even when the
  // same prompt is requested twice.
  const [seedPrompt, setSeedPrompt] = useState<string | null>(null);
  const [seedNonce, setSeedNonce] = useState(0);
  useEffect(() => {
    function onOpenChat(e: Event) {
      const detail = (e as CustomEvent<{ prompt?: string }>).detail;
      setOpen(true);
      const p = detail?.prompt?.trim();
      if (p) {
        setSeedPrompt(p);
        setSeedNonce((n) => n + 1);
      }
    }
    window.addEventListener("retention:open-chat", onOpenChat as EventListener);
    return () =>
      window.removeEventListener("retention:open-chat", onOpenChat as EventListener);
  }, []);

  // Brand name for the header subtitle. Shares the cache key with AppShell's
  // lookup so this is a dedup, not a new fetch.
  const brandQuery = useQuery({
    queryKey: ["brand", slug],
    queryFn: () => api.getBrand(slug ?? ""),
    enabled: !!slug,
  });

  // Don't render the widget when not in brand context.
  if (!slug) return null;

  const chatSrc = seedPrompt
    ? chatEmbeddedSeedUrl(slug, seedPrompt)
    : chatEmbeddedUrl(slug);
  const chatFullUrl = chatUrl(slug);

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end">
      {open ? (
        <div className="w-[420px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] bg-bg-card border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden">
          {/* Header — gradient amber bar with leading icon, title + brand
              subtitle, "Open in full view" + close controls. Mirrors
              FloatingChat's header (the "Open in full view" link is the
              user-requested full-page entry from the widget). */}
          <div className="brand-gradient rounded-t-lg px-3 py-2.5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="size-8 rounded-md bg-white/15 flex items-center justify-center flex-shrink-0">
                <Sparkles size={16} className="text-text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-text-white leading-tight">
                  RetentionIQ AI
                </div>
                <div className="text-[11px] text-white/75 leading-tight truncate">
                  {brandQuery.data?.name ?? "Klaviyo retention"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Link
                to={chatFullUrl}
                aria-label="Open RetentionIQ AI in full view"
                title="Open in full view"
                className="p-1.5 rounded-md text-white/80 hover:text-text-white hover:bg-white/15 transition-colors"
              >
                <ExternalLink size={15} />
              </Link>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-white/80 hover:text-text-white hover:bg-white/15 transition-colors"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Chat iframe — embedded mode hides the conv-list sidebar + page
              header so the full-page Chat fills the 420px-wide widget. Same
              origin so cookies + auth flow work without sandbox restrictions.
              Re-keying by slug reloads the iframe when the brand changes
              (different brand = different chat scope). */}
          <iframe
            key={`${slug}-${seedNonce}`}
            src={chatSrc}
            title="RetentionIQ AI"
            className="flex-1 w-full border-0 bg-bg-base"
          />
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="bg-bg-card border border-border hover:bg-bg-hover rounded-full p-3 shadow-lg flex items-center gap-2"
          aria-label="Open chat advisor"
        >
          <MessageSquare size={16} className="text-text-primary" />
          <span className="text-sm text-text-primary pr-1">Advisor</span>
        </button>
      )}
    </div>
  );
}
